import { addCategories, editCategory } from 'actions';
import EmptyTableState from 'components/EmptyTableState';
import SelectOutcomeModal from 'components/Outcome/SelectOutcomeModal';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getCategoriesByRuleBulkOutcomes,
  getSelectedCustomerOutcomes,
  getSelectedUseCaseOutcomes,
} from 'selectors/bulkOutcomes';
import { useSelector } from 'store';
import type { Category, Outcome, RuleRevision, UUID } from 'types';
import { useHistory } from 'utils/urls';
import { v4 } from 'uuid';
import CategoryJsonEditorModal from './CategoryJsonEditorModal';
import CategoryListActionRow from './CategoryListActionRow';
import OutcomeListItem from './CategoryListItem';

interface Props {
  rule: RuleRevision;
}

const OutcomesRuleItem: React.VFC<Props> = ({ rule }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const customer = useSelector(getSelectedCustomerOutcomes);
  const model = useSelector(getSelectedUseCaseOutcomes);
  const categories: Category[] = useSelector((state) =>
    getCategoriesByRuleBulkOutcomes(state, rule.rule_group_uuid)
  );

  const [selectedCategories, setSelectedCategories] = useState<UUID[]>([]);
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);
  const [isAddingOutcome, setIsAddingOutcome] = useState(false);

  const currentCategory = categories.find((c) => selectedCategories.includes(c.uuid));

  const toggleJsonModal = (): void => setIsJsonModalOpen(!isJsonModalOpen);
  const toggleOutcomeModal = (): void => setIsAddingOutcome(!isAddingOutcome);

  const handleAdvanced = (newConfig: Category['config']): void => {
    if (currentCategory) {
      dispatch(
        editCategory({
          category: { ...currentCategory, config: { ...currentCategory.config, ...newConfig } },
          ruleId: currentCategory.rule?.rule_group_uuid || '',
        })
      );
    }
  };

  const handleAddOutcome = (outcome: { name: string; uuid: string; type?: string }): void => {
    dispatch(
      addCategories({
        categories: [
          {
            uuid: v4(),
            config: {
              version: '0.1',
              active_gmail: false,
              active_chrome: false,
              active_slack: false,
              active_o365: false,
              active_o365_teams: false,
              active_zendesk: false,
              active_glia: false,
              active_outbound: false,
              active_inbound: false,
              active_api: false,
              active_app: false,
              active_realtime: false,
              active_attachment_extensions: null,
              valid_for_seconds: null,
              cooldown_time_seconds: null,
            } as Category['config'],
            outcome_uuid: outcome.uuid,
            outcome: outcome as Outcome,
            rule_revision_uuid: rule.uuid,
            model_uuid: model?.uuid || '',
            customer_uuid: customer?.uuid || '',
            rule,
          },
        ],
        rule,
      })
    );
  };

  const handleCreate = (): void => {
    history.pushLookup({
      routeName: 'model-rule-group-outcome-id',
      routeParams: { modelId: model?.uuid || '', ruleId: rule.rule_group_uuid, outcomeId: 'new' },
      queryParams: { from_bulk: 'true' },
      customerDomain: customer?.domain || '',
    });
  };

  return (
    <>
      <div className="flex flex-col gap-2">
        <span className="text-heading-3">{rule.name}</span>
        <div className="table-wrapper__new custom-scrollbar overflow-auto">
          <table className="table-wrapper__table table-wrapper_table--fixed">
            <thead className="sticky top-0 z-0">
              <tr>
                <th className="table-wrapper__new-th py-0 w-13">
                  <div className="w-full pr-2 flex justify-end">
                    <input
                      id="checkbox-tag-groups"
                      data-testid="checkbox"
                      type="checkbox"
                      className={`cursor-pointer form-checkbox litlingo-checkbox w-5 h-5 rounded-sm ${
                        selectedCategories?.length > 0 &&
                        selectedCategories?.length < categories.length
                          ? 'checkbox-select-all-partial'
                          : ''
                      }`}
                      onClick={(e): void => e.stopPropagation()}
                      onChange={(): void => {
                        if (selectedCategories.length === categories.length) {
                          setSelectedCategories([]);
                        } else {
                          setSelectedCategories(categories.map((t) => t.uuid));
                        }
                      }}
                      checked={
                        selectedCategories?.length === categories.length &&
                        selectedCategories?.length > 0
                      }
                    />
                  </div>
                </th>
                <th className="table-wrapper__new-th">Name</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-16">Recipient Type</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">MS Teams</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">Slack</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">Gmail</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">Chrome</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">Glia</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">MS O365</th>
                <th className="table-wrapper__new-th table-wrapper__th--w-8">Inbound</th>
              </tr>
              {selectedCategories?.length >= 1 && (
                <CategoryListActionRow
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                  rule={rule}
                  toggleJsonModal={toggleJsonModal}
                />
              )}
            </thead>
            <tbody className="table-wrapper__tbody">
              {categories.length === 0 ? (
                <EmptyTableState message="There are no outcomes to display" colCount="10" />
              ) : (
                categories.map((category) => (
                  <OutcomeListItem
                    key={category.uuid}
                    category={category}
                    selected={selectedCategories.includes(category.uuid)}
                    setSelectedCategories={setSelectedCategories}
                    rule={rule}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
        <button
          type="button"
          className="button button--secondary font-bold h-8 w-37 justify-center self-end"
          onClick={toggleOutcomeModal}
        >
          Add Outcome
        </button>
      </div>
      {isAddingOutcome && (
        <SelectOutcomeModal
          toggleModalOpen={toggleOutcomeModal}
          onSelect={handleAddOutcome}
          allowCreate
          onCreate={handleCreate}
        />
      )}
      {isJsonModalOpen && currentCategory && (
        <CategoryJsonEditorModal
          setOpenModal={setIsJsonModalOpen}
          defaultConfig={currentCategory.config}
          category={currentCategory}
          handleSave={handleAdvanced}
        />
      )}
    </>
  );
};

export default OutcomesRuleItem;
